// src/components/opportunity/useOpportunityWorkspace.ts
//
// State + data loader for the Opportunity workspace. Centralizes the
// `/api/opportunity/get` call so the workspace shell + each tab can
// consume the same loaded opportunity + active snapshot + revisions
// without re-fetching. Tabs trigger a refetch via `refresh` /
// `refreshAsync` (the latter returns the fresh payload for callers
// that must generate a PDF against the just-saved snapshot).

import { useCallback, useEffect, useState } from "react";
import {
  getOpportunity,
  type OpportunityGetResponse,
} from "@/lib/opportunity/opportunityClient";

export type OpportunityWorkspaceState = {
  loading: boolean;
  error: string | null;
  data: OpportunityGetResponse | null;
  refresh: () => void;
  refreshAsync: () => Promise<OpportunityGetResponse | null>;
};

export function useOpportunityWorkspace(opportunityId: string): OpportunityWorkspaceState {
  const [data, setData] = useState<OpportunityGetResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  const refreshAsync = useCallback(async (): Promise<OpportunityGetResponse | null> => {
    setLoading(true);
    setError(null);
    const r = await getOpportunity(opportunityId);
    if (r.ok) {
      setData(r.data);
      setLoading(false);
      return r.data;
    }
    setData(null);
    setError(r.error);
    setLoading(false);
    return null;
  }, [opportunityId]);

  useEffect(() => {
    const flag = { value: false };
    void (async () => {
      setLoading(true);
      setError(null);
      const r = await getOpportunity(opportunityId);
      if (flag.value) return;
      if (r.ok) {
        setData(r.data);
      } else {
        setData(null);
        setError(r.error);
      }
      setLoading(false);
    })();
    return () => {
      flag.value = true;
    };
  }, [opportunityId, tick]);

  const refresh = useCallback(() => {
    setTick((t) => t + 1);
  }, []);

  return { loading, error, data, refresh, refreshAsync };
}
